import {
  FieldTypeAsset,
  FieldTypeLink,
  VehicleCardBackground,
  ViewModes,
} from '@allesauto/utils/consts';
import {
  getUrlFromStoryblokLink,
  parseSlashToURL,
} from '@allesauto/utils/funcs';
import { Routes_DE } from '@allesauto/utils/routes';
import { ArrowRight32 } from '@carbon/icons-react';
import clsx from 'clsx';
import React from 'react';

import NextImage from '../../nextImage';
import NextLink from '../../nextLink';
import Price from '../../price';

export interface VehicleCardProps {
  headline: string;
  condition?: string;
  imageUrl?: string | FieldTypeAsset;
  imageAlt?: string;
  hasImageFullscreen?: boolean;
  make?: string;
  model?: string;
  vehicleId?: string;
  price?: number;
  monthlyPrice?: number;
  priceText?: string;
  monthlyText?: string;
  background?: VehicleCardBackground;
  backgroundUrl?: string;
  link?: string | FieldTypeLink;
  fixedHeightValue?: number;
  imageObjectFit?: 'cover';
}

const VehicleCard: React.FC<VehicleCardProps> = ({
  headline,
  condition,
  background = VehicleCardBackground.WHITE,
  imageUrl,
  imageAlt,
  hasImageFullscreen = false,
  make,
  model,
  vehicleId,
  price,
  monthlyPrice,
  priceText,
  monthlyText,
  backgroundUrl,
  link,
  imageObjectFit,
}) => {
  const theme = {
    WHITE: {
      background: 'bg-white',
      text: 'text-purple80 monochrome:text-grey90',
      border: 'bg-green60  monochrome:bg-grey20 wl-bg-highlight',
      arrow: 'text-purple80  monochrome:text-grey90',
    },
    GREEN: {
      background: 'bg-green60 monochrome:bg-grey20',
      text: 'text-purple80 monochrome:text-grey90',
      border: 'bg-purple80 monochrome:bg-white wl-bg-highlight',
      arrow: 'text-purple80 monochrome:text-grey90',
    },
    BLACK: {
      background: 'bg-grey80',
      text: 'text-white',
      border: 'bg-green60 monochrome:bg-grey20 wl-bg-highlight',
      arrow: 'text-green60 monochrome:text-white',
    },
    PURPLE: {
      background: 'bg-purple80 monochrome:bg-grey90',
      text: 'text-green60 monochrome:!text-white',
      border: 'bg-green60 monochrome:bg-grey20 wl-bg-highlight',
      arrow: 'text-green60 monochrome:text-white',
    },
  };

  let cardRef = '';

  if (link) {
    if (typeof link === 'string') {
      cardRef = link;
    } else {
      hasImageFullscreen = true;
      const url = link && getUrlFromStoryblokLink(link);
      cardRef = (url && parseSlashToURL(url)) || '';
    }
  } else {
    cardRef = `${Routes_DE.PDP_VEHICLES}/${make}/${model}/${vehicleId}`;
  }

  const className = {
    wrapper: clsx(
      'aspect-square cursor-pointer flex flex-col group grow justify-between no-underline pb-20px pt-40px px-25px relative lg:flex-[1_1_0] lg:px-40px lg:w-0',
      theme[background].background
    ),
    headline: clsx(
      'font-headline mb-20px overflow-hidden',
      imageUrl && 'text-headline4',
      !imageUrl && 'text-headline2 mb-30px',
      theme[background].text
    ),
    text: clsx('mb-5px lg:mb-10px', theme[background].text),
    image: clsx(
      hasImageFullscreen
        ? 'absolute top-0 left-0 w-full min-h-[280px] lg:min-h-[380px] h-full'
        : 'relative mb-20px'
    ),
    priceWrapper: clsx(
      'flex flex-row-reverse justify-between',
      !monthlyPrice && 'mb-15px lg:mb-20px'
    ),
    price: {
      mainPriceClass:
        background === VehicleCardBackground.BLACK ? 'text-white' : '',
      monthlyPriceClass:
        background === VehicleCardBackground.BLACK ? 'text-white' : '',
    },
    border: clsx(
      'absolute bottom-0 duration-[400ms] ease-linear h-0 left-0 mt-0 overflow-hidden transition-all w-full z-10 lg:group-focus:h-[7px] lg:group-focus:mt-[-7px] lg:group-hover:h-[7px] lg:group-hover:mt-[-7px]',
      theme[background].border
    ),
    arrow: clsx(
      'bottom-[36px] duration-300 font-weight-bold hidden opacity-0 right-[40px] self-start transition-all z-10 lg:block lg:group-focus:opacity-100 lg:group-hover:opacity-100',
      theme[background].arrow
    ),
  };

  return (
    <NextLink
      href={cardRef}
      target={typeof link === 'string' ? '_self' : link?.target}
      passHref
      className={className.wrapper}
    >
      {backgroundUrl && (
        <NextImage useCustomLoader={false} layout="fill" src={backgroundUrl} />
      )}
      <div className={clsx('z-10', backgroundUrl && 'mt-[-80px]')}>
        <div className={className.text}>{condition}</div>
        <div className={className.headline}>{headline}</div>
      </div>
      {imageUrl && (
        <div className={className.image}>
          <NextImage
            useCustomLoader={false}
            layout="fill"
            alt={typeof imageUrl === 'string' ? imageAlt || '' : imageUrl?.alt}
            src={typeof imageUrl === 'string' ? imageUrl : imageUrl?.filename}
            objectFit={imageObjectFit}
          />
        </div>
      )}
      <div className={className.priceWrapper}>
        {price && (
          <Price
            price={price}
            displayView={ViewModes.GRID}
            monthlyPrice={monthlyPrice}
            priceTextKey={priceText || 'ui.PRICE.PRICE_ONLY'}
            monthlyTextKey={monthlyText || 'ui.PRICE.PRICE_ONLY'}
            isMonthlyMainPrice={!!monthlyPrice}
            classNames={className.price}
            isTeaserPrice={true}
          />
        )}
        <ArrowRight32 className={className.arrow} />
      </div>
      <div className={className.border}></div>
    </NextLink>
  );
};

export default VehicleCard;
